import type { CoasterRuntime, RuntimeSnapshot } from '../app/runtime-types.ts';
import { Button, Dialog } from './primitives.tsx';

export function HelpCenter({
  open,
  state,
  runtime,
  onClose,
  onStartCreatorGuide,
  onStartFinishChallenge,
  creatorGuideAvailable,
}: {
  readonly open: boolean;
  readonly state: RuntimeSnapshot;
  readonly runtime: CoasterRuntime | null;
  readonly onClose: () => void;
  readonly onStartCreatorGuide: () => void;
  readonly onStartFinishChallenge: () => void;
  readonly creatorGuideAvailable: boolean;
}) {
  if (!open) return null;
  return (
    <Dialog
      id="help-center"
      className="help-center-dialog"
      title="Help, Settings & Privacy"
      description="Short guides and comfort controls for building and riding."
      onClose={onClose}
    >
      <div class="help-center-content">
        <section>
          <h3>Learn to build</h3>
          {creatorGuideAvailable ? (
            <p>
              Build a complete first coaster step by step, then test it to unlock Recording and
              Publishing.
            </p>
          ) : (
            <p>
              The guided walkthrough is available on a larger screen. Mobile building remains fully
              available without it.
            </p>
          )}
          <div class="help-center-actions">
            {creatorGuideAvailable && (
              <Button variant="primary" type="button" onClick={onStartCreatorGuide}>
                &quot;Build Your First Coaster&quot; Walkthrough
              </Button>
            )}
            <Button type="button" onClick={onStartFinishChallenge}>
              Try Completing a Half-Built Coaster
            </Button>
          </div>
        </section>

        <section>
          <h3>Sound & comfort</h3>
          <label class="help-toggle">
            <input
              type="checkbox"
              checked={state.settings.muted}
              onChange={(event) => runtime?.setMuted(event.currentTarget.checked)}
            />
            Mute all sound
          </label>
          <label class="help-toggle">
            <input
              type="checkbox"
              checked={state.settings.reducedMotion}
              onChange={(event) => runtime?.setReducedMotion(event.currentTarget.checked)}
            />
            Reduce camera motion
          </label>
          <div class="help-mix-grid help-camera-grid">
            <label>
              <span>Field of view</span>
              <input
                type="range"
                aria-label="Field of view"
                min="50"
                max="75"
                value={state.settings.cameraFov}
                onInput={(event) => runtime?.setCameraFov(Number(event.currentTarget.value))}
              />
            </label>
            <label>
              <span>Camera motion</span>
              <input
                type="range"
                aria-label="Camera motion"
                min="0"
                max="1"
                step="0.05"
                value={state.settings.cameraShake}
                onInput={(event) => runtime?.setCameraShake(Number(event.currentTarget.value))}
              />
            </label>
          </div>
          <div class="help-mix-grid">
            {(
              [
                ['masterVolume', 'Master'],
                ['musicVolume', 'Music'],
                ['rideVolume', 'Coaster sounds'],
                ['environmentVolume', 'Environment'],
                ['uiVolume', 'UI'],
              ] as const
            ).map(([key, label]) => (
              <label key={key}>
                <span>{label}</span>
                <input
                  type="range"
                  aria-label={`${label} volume`}
                  min="0"
                  max="1"
                  step="0.05"
                  value={state.settings[key]}
                  onInput={(event) => {
                    const value = Number(event.currentTarget.value);
                    if (key === 'masterVolume') runtime?.setMasterVolume(value);
                    else runtime?.setAudioBusVolume(key, value);
                  }}
                />
              </label>
            ))}
          </div>
        </section>

        <section>
          <h3>Camera, privacy & reporting</h3>
          <p>
            Reaction Cam is optional and requested only when you choose it for Record Ride. Camera
            or microphone denial never blocks ordinary building, Test Ride, or riding.
          </p>
          <p>
            The microphone is never played back live. It is included only in a recording when you
            explicitly enable it, and you can discard the recording before sharing.
          </p>
          <p>
            Core building, riding, and ordinary Ride Media do not require a purchase. Optional
            offers remain unavailable when Buncha Games does not supply the required capability.
          </p>
          <h4>Report a coaster</h4>
          <p>
            Content reporting is handled by Buncha Games. This game will expose the platform report
            action when Buncha Games supplies the approved reporting contract; no report is sent
            from this QA build.
          </p>
        </section>
      </div>
    </Dialog>
  );
}
